import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { makeServerProps } from '@/server/query'

import SeriesQ from '@/queries/SeriesQ'
import SeriesTagQ from '@/queries/SeriesTagQ'
import { useQuery } from '@/client/context/query'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import {
  hasSummerCampEnded,
  SUMMER_CAMP_COLORS,
  SUMMER_CAMP_HERO_BG_SRC,
  SUMMER_CAMP_HERO_LOGO_SRC,
  SUMMER_CAMP_YEAR,
} from '@/client/services/summerCamp'
import { getPublicUrl, getSummerCampExploreUrl } from '@/client/services/url'

import BrandMark from '@/components/Icons/BrandMark'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import SeriesBoxFromQuery from '@/components/Series/SeriesBox/SeriesBoxFromQuery'
import ShwLink from '@/components/ShwLink'
import SummerCampAbout from '@/components/SummerCamp/SummerCampAbout'
import SummerCampFAQ from '@/components/SummerCamp/SummerCampFAQ'
import SummerCampHowToParticipate from '@/components/SummerCamp/SummerCampHowToParticipate'

const SummerCamp: React.FC = () => {
  const query = useQuery()

  const featuredSeries = query
    .many(
      SeriesTagQ.where(
        {
          include: [
            {
              association: 'Series',
              required: true,
            },
          ],
          order: [[SQL`(SELECT ${SeriesQ.countRegistrations()})`, 'desc']],
          limit: 3,
        },
        SQL`${SeriesTagQ.isSummerCampFeaturedTag()} AND ${SeriesQ.hasRoomForLearners()}`
      )
    )
    .map(q => ({ queryRow: q, type: 'series', key: q(SeriesQ.id) ?? 0 }))

  return (
    <Layout title="Summer Camp">
      <Flex
        alignItems="center"
        bgColor={SUMMER_CAMP_COLORS.primaryColor}
        bgImage={getPublicUrl(SUMMER_CAMP_HERO_BG_SRC)}
        bgSize="cover"
        position="relative"
        overflowX="hidden"
        overflowY="hidden"
      >
        <Container zIndex={1}>
          <Center>
            <Stack textAlign="center" align="center">
              {/* Hide the logo on mobile because it doesn't work well with the 2025 background. We can remove this logic in the future! */}
              <Text
                color="primary.contrast"
                fontWeight="bold"
                display={{ base: 'none', md: 'block' }}
                mb="24px"
                mt="16px"
              >
                <BrandMark textColor="primary.contrast" height="32px" />
              </Text>
              {/* Spacer box that substitutes for the logo being hidden on mobile */}
              <Box height="72px" display={{ base: 'block', md: 'none' }} />
              <PublicImage
                src={SUMMER_CAMP_HERO_LOGO_SRC}
                width="360px"
                alt={`Schoolhouse Summer Camp ${SUMMER_CAMP_YEAR}`}
              />
              <Stack direction="column" spacing="24px">
                <Heading
                  as="h2"
                  color="primary.contrast"
                  size="lg"
                  fontWeight="semibold"
                  maxWidth="400px"
                  textAlign="center"
                >
                  Have fun, learn something new, and make friends from all across the world.
                </Heading>
                <Box mb="24px">
                  <ShwLink href={getSummerCampExploreUrl()} mb="24px">
                    <Button variant="white">Explore Sessions</Button>
                  </ShwLink>
                </Box>
              </Stack>
            </Stack>
          </Center>
        </Container>
      </Flex>
      <Flex alignItems="center" bgColor={SUMMER_CAMP_COLORS.contrastColor}>
        <Container>
          <SummerCampAbout />
        </Container>
      </Flex>
      <Flex alignItems="center" bgColor="bg.subtle">
        <Container>
          <SummerCampHowToParticipate />
        </Container>
      </Flex>
      <Flex alignItems="center">
        <Container>
          <Stack alignItems="center" my="32px">
            <Heading size="2xl" mb={4}>
              Summer Camp Series
            </Heading>
            {hasSummerCampEnded() ? (
              <Text fontWeight="bold" color="fg.muted">
                Summer Camp {SUMMER_CAMP_YEAR} was a smashing success! Stay tuned for{' '}
                {SUMMER_CAMP_YEAR + 1}.
              </Text>
            ) : (
              <>
                <Text>Registrations are limited—grab your spot today!</Text>
                <Box>
                  <SimpleGrid spacing={['16px', '24px', '32px']} columns={[1, 1, 2, 3]} my="32px">
                    {featuredSeries.slice(0, 3).map(({ queryRow, key }) => (
                      <Box key={key}>
                        <SeriesBoxFromQuery series={queryRow} />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
                <ShwLink href={getSummerCampExploreUrl()} tabIndex={-1}>
                  <Flex px="20px" py="8px" justifyContent={['left', 'left', 'center', 'left']}>
                    <Button rightIcon={<FontAwesomeIcon icon={faArrowRight} />}>
                      See All Series
                    </Button>
                  </Flex>
                </ShwLink>
              </>
            )}
          </Stack>
        </Container>
      </Flex>
      <Flex alignItems="center">
        <Container>
          <SummerCampFAQ />
        </Container>
      </Flex>
    </Layout>
  )
}

const SummerCampPage = makePage(SummerCamp, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(SummerCampPage)
export default SummerCampPage
